import React from "react";
import {
  honor_1,
  honor_2,
  honor_3,
  honor_4,
  honor_5,
  honor_6,
} from "../../constants/icons";
import {
  summariseCareerExperience,
  formatCareerDuration,
  formatExcludedStationsNote,
} from "../../utils/careerExperience";

// Same copy-to-clipboard behaviour as the twin component
// src/components/profile/career-timeline/ReferencesSection.js (its own
// editable-references variant of this exact REFERENCES block).
const handleCopyToClipboard = (text) => {
  if (text) navigator.clipboard.writeText(text);
};

const StaffCareerTimeline = ({ staff }) => {
  // Use actual staff data from props
  const actualData = {
    experience: staff?.experience || [],
    additionalNotes: staff?.additionalNotes || staff?.coaching_notes || "No additional notes provided.",
    achievements: (staff?.achievements || staff?.trophies || []).map((trophy, index) => ({
      id: index + 1,
      tournament_image: null, // Remove placeholder image
      league_name: trophy.title || trophy.league_name || "Achievement",
      club_name: trophy.club || trophy.club_name || "Club",
      years: trophy.season || trophy.years || "Year not specified",
    })),
    honors: (staff?.honors || staff?.honors_and_awards || []).map((honor, index) => ({
      id: index + 1,
      title: honor.title || "Honor",
      year: honor.year || "Year not specified",
      icon: `honor_${(index % 6) + 1}`,
    })),
    contributions: (staff?.contributions || staff?.notable_contributions || []).map((contribution, index) => ({
      id: index + 1,
      description: typeof contribution === 'string' ? contribution : contribution.description || "Contribution",
    })),
    references: (staff?.references || []).map((reference, index) => ({
      id: index + 1,
      name: reference.name || "Reference",
      phone: reference.phone_number || reference.phone || "Phone not provided",
      email: reference.email || "Email not provided",
      hasRecommendation: reference.hasRecommendation || false,
    })),
    publications: (staff?.publications || []).map((publication, index) => ({
      id: index + 1,
      title: publication.title || "Publication",
      type: publication.type || "article",
      link: publication.link || "#",
    })),
    media: [], // Media will be populated from documents if needed
  };

  // Total experience, summed from the stations that actually carry dates.
  // Stations without a usable start date are skipped, never estimated.
  const experienceSummary = summariseCareerExperience(actualData.experience);
  const totalExperienceLabel =
    experienceSummary.counted > 0 ? formatCareerDuration(experienceSummary.totalMonths) : null;
  const excludedStationsNote = totalExperienceLabel
    ? formatExcludedStationsNote(experienceSummary.skipped)
    : null;

  // Check if resume document exists
  const hasResumeDocument = () => {
    if (!staff?.documents || !Array.isArray(staff.documents)) return false;
    return staff.documents.some(doc => doc.doc_type === 'resume');
  };

  // Get resume document for download
  const getResumeDocument = () => {
    if (!staff?.documents || !Array.isArray(staff.documents)) return null;
    return staff.documents.find(doc => doc.doc_type === 'resume');
  };

  const getBackgroundStyle = (index) => {
    const backgrounds = [
      "linear-gradient(180deg, #FCF7FF 0%, #F7E9FF 100%)",
      "linear-gradient(180deg, rgba(231, 247, 255, 0.4) 0%, rgba(169, 225, 255, 0.4) 100%)",
      "linear-gradient(180deg, rgba(255, 221, 221, 0.4) 0%, rgba(255, 177, 177, 0.4) 100%)",
      "linear-gradient(180deg, #FFDBEB 0%, #FFD8EA 100%)",
      "linear-gradient(180deg, rgba(255, 240, 213, 0.4) 0%, rgba(255, 208, 130, 0.4) 100%)",
      "linear-gradient(180deg, #F8FFF7 0%, #E2FFE8 100%)",
    ];
    return backgrounds[index % backgrounds.length];
  };

  const getBorderStyle = (index) => {
    const borders = [
      "border-[#E7BCFF]",
      "border-[#9FDDFF]",
      "border-[#FFC2C2]",
      "border-[#FD89CB]",
      "border-[#FFD082]",
      "border-[#7FFE9B]",
    ];
    return borders[index % borders.length];
  };

  const getHonorIcon = (index) => {
    const icons = [honor_1, honor_2, honor_3, honor_4, honor_5, honor_6];
    return icons[index % icons.length];
  };

  return (
    <div className="space-y-8 bg-[#F8F9FB] p-5 rounded-xl">
      {/* EXPERIENCE Section */}
      <div className="bg-white p-6 rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[14px] font-bold text-[#222A35]">EXPERIENCE</h2>
          {hasResumeDocument() && (
            <button 
              onClick={() => {
                const resumeDoc = getResumeDocument();
                if (resumeDoc && resumeDoc.url) {
                  window.open(resumeDoc.url, '_blank');
                }
              }}
              className="bg-[#FFFFFF] text-[#1E1E1E] px-4 py-2 rounded-lg text-sm font-medium border border-[#E6E6E6] hover:bg-gray-50 transition-colors"
            >
              Download resume
            </button>
          )}
        </div>
        <div className="space-y-4">
          {actualData.experience.length > 0 ? (
            <>
              {actualData.experience.map((exp) => (
                <div key={exp.id} className="border-b border-[#D6DBEA] p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2283FF] bg-opacity-10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-[#2283FF] text-lg font-bold">
                        {exp.club_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="text-[#222A35] text-[20px] font-bold">
                        {exp.position_name}
                      </div>
                      <div className="text-[#222A35] text-[16px] font-bold">
                        {exp.club_name}
                      </div>
                      <div className="text-[#646B7E] text-[14px]">
                        {exp.start_date} - {exp.end_date} | {exp.place} | {exp.type}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {totalExperienceLabel && (
                <div className="space-y-1">
                  <div
                    className="px-4 py-2 w-fit rounded-lg border border-[#FF4D4F30]"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(244, 227, 237, 0.1) 0%, rgba(246, 167, 85, 0.1) 100%)",
                    }}
                  >
                    <span className="text-[#646B7E] text-[14px]">
                      Total: {totalExperienceLabel}
                    </span>
                  </div>
                  {excludedStationsNote && (
                    <div className="text-[#8A90A0] text-[12px]">
                      {excludedStationsNote}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="p-4 text-center">
              <span className="text-[#646B7E] text-[14px]">
                No work experience listed yet
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ADDITIONAL NOTES Section */}
      <div className="bg-white p-6   rounded-lg">
        <div className="mb-6">
          <h2 className="text-[14px] font-bold text-[#222A35] mb-4">
            ADDITIONAL NOTES
          </h2>
          <div className="w-full rounded-lg text-[#1A212F] text-[16px] font-medium">
            {actualData.additionalNotes}
          </div>
        </div>
      </div>

      {/* ACHIEVEMENTS / TROPHIES Section */}
      <div className="bg-white p-6  rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[14px] font-bold text-[#222A35]">
            ACHIEVEMENTS / TROPHIES
          </h2>
        </div>
        <div className="rounded-xl">
          {actualData.achievements.length > 0 ? (
            // Same trophy cards as the coach profile, and they broke the same
            // way on a phone. Kept in step with AchievementsSection.js.
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {actualData.achievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className="border border-[#BCCAD7] bg-[#F8F9FB] p-4 rounded-xl flex gap-4"
                >
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <div className="w-full h-full bg-yellow-500 flex items-center justify-center">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9H4.5C4.5 6.51472 6.51472 4.5 9 4.5H15C17.4853 4.5 19.5 6.51472 19.5 9H18C18 7.34315 16.6569 6 15 6H9C7.34315 6 6 7.34315 6 9Z"
                          fill="white"
                        />
                        <path
                          d="M12 4.5V6M12 18V19.5M4.5 12H6M18 12H19.5M7.5 7.5L8.5 8.5M15.5 15.5L16.5 16.5M8.5 15.5L7.5 16.5M16.5 7.5L15.5 8.5"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M12 6C14.2091 6 16 7.79086 16 10V12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12V10C8 7.79086 9.79086 6 12 6Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 min-w-0 break-words">
                    <span className="text-[#222A35] text-[20px] font-bold">
                      {achievement.league_name}
                    </span>
                    <span className="text-[#222A35] text-[16px] font-bold">
                      {achievement.club_name}
                    </span>
                    <span className="text-[#646B7E] text-[14px]">
                      {achievement.years}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center">
              <span className="text-[#646B7E] text-[14px]">
                No achievements listed yet
              </span>
            </div>
          )}
        </div>
      </div>

      {/* HONOR & AWARDS Section */}
      <div className="bg-white p-6  rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[14px] font-bold text-[#222A35]">
            HONOR & AWARDS
          </h2>
        </div>
        {actualData.honors.length > 0 ? (
          <div className="grid grid-cols-4 gap-4">
            {actualData.honors.map((honor, index) => (
              <div
                key={honor.id}
                className={`rounded-xl p-4 gap-4 flex items-center text-center relative border ${getBorderStyle(
                  index
                )}`}
                style={{ background: getBackgroundStyle(index) }}
              >
                <div className="">
                  <img
                    src={getHonorIcon(index)}
                    alt="Honor Icon"
                    className="w-16 h-16"
                  />
                </div>
                <div>
                  <div className="text-[#222A35] text-[16px] font-bold">
                    {honor.title}
                  </div>
                  <div className="text-[#646B7E] text-[14px]">{honor.year}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center">
            <span className="text-[#646B7E] text-[14px]">
              No honors & awards listed yet
            </span>
          </div>
        )}
      </div>

      {/* NOTABLE CONTRIBUTION Section */}
      <div className="bg-white p-6  rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[14px] font-bold text-[#222A35]">
            NOTABLE CONTRIBUTION
          </h2>
        </div>
        <div className="space-y-4">
          {actualData.contributions.length > 0 ? (
            actualData.contributions.map((contribution) => (
              <div
                key={contribution.id}
                className="bg-[#F8F9FB] p-4 rounded-lg flex items-center justify-between"
              >
                <div className="text-[#222A35] text-[16px] font-bold">
                  {contribution.description}
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 text-center">
              <span className="text-[#646B7E] text-[14px]">
                No notable contributions listed yet
              </span>
            </div>
          )}
        </div>
      </div>

      {/* REFERENCES Section */}
      <div className="bg-white p-6  rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[14px] font-bold text-[#222A35]">REFERENCES</h2>
        </div>
        <div className="space-y-6">
          {actualData.references.length > 0 ? (
            actualData.references.map((reference) => (
              <div
                key={reference.id}
                className="border-b border-[#D6DBEA] pb-6] "
              >
                <div className=" p-4 bg-[#F8F9FB] rounded-lg w-fit mb-2">
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-[#222A35] text-[20px] font-bold">
                      {reference.name}
                    </div>
                  </div>

                  <div className="flex gap-4 items-center">
                    {/* Phone Number */}
                    <div className="flex items-center gap-3">
                      <span className="text-[#646B7E] text-[14px]">
                        {reference.phone}
                      </span>
                      <button
                        onClick={() => handleCopyToClipboard(reference.phone)}
                        className="w-6 h-6 bg-[#F8F9FB] border border-[#D6DBEA] rounded flex items-center justify-center"
                        aria-label="Copy phone number"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4 2H2C1.44772 2 1 2.44772 1 3V9C1 9.55228 1.44772 10 2 10H8C8.55228 10 9 9.55228 9 9V7M4 2C4 1.44772 4.44772 1 5 1H9C9.55228 1 10 1.44772 10 2V6C10 6.55228 9.55228 7 9 7H5C4.44772 7 4 6.55228 4 6V2Z"
                            stroke="#646B7E"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Email */}
                    <div className="flex items-center gap-3">
                      <span className="text-[#646B7E] text-[14px]">
                        {reference.email}
                      </span>
                      <button
                        onClick={() => handleCopyToClipboard(reference.email)}
                        className="w-6 h-6 bg-[#F8F9FB] border border-[#D6DBEA] rounded flex items-center justify-center"
                        aria-label="Copy email address"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4 2H2C1.44772 2 1 2.44772 1 3V9C1 9.55228 1.44772 10 2 10H8C8.55228 10 9 9.55228 9 9V7M4 2C4 1.44772 4.44772 1 5 1H9C9.55228 1 10 1.44772 10 2V6C10 6.55228 9.55228 7 9 7H5C4.44772 7 4 6.55228 4 6V2Z"
                            stroke="#646B7E"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Recommendation Letter */}
                    {reference.hasRecommendation && (
                      <div className="flex items-center gap-3">
                        <span className="text-[#646B7E] text-[14px]">
                          Letter of recommendation
                        </span>
                        <div className="w-6 h-6 bg-[#2283FF] bg-opacity-10 rounded flex items-center justify-center">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 2H2C1.44772 2 1 2.44772 1 3V9C1 9.55228 1.44772 10 2 10H10C10.5523 10 11 9.55228 11 9V3C11 2.44772 10.5523 2 10 2Z"
                              stroke="#2283FF"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M3 4H9M3 6H9M3 8H6"
                              stroke="#2283FF"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 text-center">
              <span className="text-[#646B7E] text-[14px]">
                No references listed yet
              </span>
            </div>
          )}
        </div>
      </div>

      {/* PUBLICATIONS/MEDIA Section */}
      <div className="bg-white p-6  rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[14px] font-bold text-[#222A35]">
            PUBLICATIONS / MEDIA
          </h2>
        </div>
        <div className="space-y-4">
          {actualData.publications.length > 0 ? (
            actualData.publications.map((publication) => (
              <div
                key={publication.id}
                className="border-b border-[#D6DBEA] p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className="text-[#222A35] text-[16px] font-bold">
                    {publication.title}
                  </span>
                  <div className="w-8 h-8 bg-[#2283FF] bg-opacity-10 rounded-lg flex items-center justify-center">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 8.66667V12.6667C12 13.0203 11.8595 13.3594 11.6095 13.6095C11.3594 13.8595 11.0203 14 10.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V5.33333C2 4.97971 2.14048 4.64057 2.39052 4.39052C2.64057 4.14048 2.97971 4 3.33333 4H7.33333M10 2H14V6M6 10L14 2"
                        stroke="#2283FF"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 text-center">
              <span className="text-[#646B7E] text-[14px]">
                No publications listed yet
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffCareerTimeline;
